import { fetchDocumentContent, fetchLectureData } from './googleSheets';

/**
 * Cache for parsed takeaways to avoid redundant fetching
 */
const takeawaysCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Patterns that indicate metadata/header content, NOT actual takeaways.
 * These appear in the DOCX header section generated by the backend.
 */
const METADATA_PATTERNS = [
  // Date patterns: "Mar 05, 2026", "March 5, 2026", "2026-03-05", etc.
  /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+\d{1,2},?\s+\d{4}\b/i,
  /\b\d{4}[-/]\d{2}[-/]\d{2}\b/,
  // Pipe-separated metadata: "Speaker Name | Mar 05, 2026"
  /^[^|]{3,50}\s*\|\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
  // Event/series names with year: "Ramadan 2026 - ...", "Muharram 1447 - ..."
  /^(Ramad[h]?an|Muharram|Safar|Rajab|Shaban|Fatimiy+ah|Jashan)\s+\d{4}/i,
  // "Lecture X Key Takeaways" title
  /^(lecture\s+\d+\s+)?key\s+takeaways?\s*$/i,
  // Just a name + date (no colon, no explanation)
  /^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?\s*\|\s*.{5,20}$/,
  // Page numbers, headers
  /^page\s+\d+/i,
  /^\d+\s*$/,
  // Very short lines that are just titles/labels
  /^(key takeaways?|5 key takeaways?|takeaways?|summary|notes|overview)\s*$/i,
];

/**
 * Check if text looks like document metadata rather than an actual takeaway.
 */
const isMetadata = (text) => {
  if (!text) return true;
  const trimmed = text.trim();

  // Too short to be a real takeaway (needs descriptor + explanation)
  if (trimmed.length < 40) return true;

  // Matches known metadata patterns
  for (const pattern of METADATA_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }

  // Contains ONLY a name/title + date (no actual content)
  // e.g., "Moulana Haider Raza Razvi | Mar 05, 2026"
  if (trimmed.includes('|') && trimmed.split('|').length === 2) {
    const parts = trimmed.split('|').map(p => p.trim());
    if (parts[1].length < 20) return true; // Right side is just a date
  }

  return false;
};

/**
 * Check if text has the structure of an actual takeaway.
 * Backend generates: "**Bold Descriptor:** Explanation text"
 * In plain text this becomes: "Bold Descriptor: Explanation text"
 */
const isTakeawayContent = (text) => {
  if (!text || text.length < 40) return false;
  const trimmed = text.trim();

  // Must have substance — real takeaways are 40+ chars with explanatory content
  // Pattern: "Descriptor: explanation" or "Descriptor – explanation"
  if (/^[A-Z][^:–—]{2,60}[:–—]\s+.{20,}/.test(trimmed)) return true;

  // Sentence-form takeaway (starts with capital, has 50+ chars, multiple words)
  if (/^[A-Z]/.test(trimmed) && trimmed.length >= 60 && trimmed.split(/\s+/).length >= 8) return true;

  return false;
};

/**
 * Extracts individual takeaway points from Google Docs HTML content.
 *
 * Strategy (in priority order):
 * 1. Look for bold text followed by colon + explanation (backend format)
 * 2. Look for list items <li> with substantial content
 * 3. Look for paragraphs matching takeaway structure
 * 4. NEVER use raw text dump — too likely to capture metadata
 *
 * @param {string} html - The HTML content from Google Docs
 * @returns {string[]} Array of individual takeaway points
 */
const extractTakeawayPoints = (html) => {
  if (!html || typeof html !== 'string') return [];

  const takeaways = [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Strategy 0: Table-based takeaways — the backend generates each takeaway as a
    // separate <table> with a <td> containing two <p> tags (descriptor + explanation).
    // This is the MOST COMMON format from the Google Docs export pipeline.
    const tableCells = doc.querySelectorAll('td');
    tableCells.forEach(td => {
      const cellParagraphs = Array.from(td.querySelectorAll('p'));
      if (cellParagraphs.length < 2) return;

      // Collect all text from this cell's paragraphs
      const texts = cellParagraphs
        .map(p => p.textContent?.trim())
        .filter(t => t && t.length > 3);

      if (texts.length < 2) return;

      // First paragraph is typically the numbered descriptor (e.g., " 1    Nobility Through Knowledge:")
      // Second paragraph is the explanation
      const descriptor = texts[0].replace(/^\s*\d+\s+/, '').replace(/^\s+/, '').trim();
      const explanation = texts.slice(1).join(' ').trim();

      // Validate: descriptor should end with colon or be short, explanation should be substantial
      if (explanation.length > 30) {
        const combined = descriptor.endsWith(':')
          ? `${descriptor} ${explanation}`
          : `${descriptor}: ${explanation}`;
        if (!isMetadata(combined) && combined.length > 50) {
          takeaways.push(combined);
        }
      }
    });

    if (takeaways.length >= 3) return takeaways;

    const paragraphs = Array.from(doc.querySelectorAll('p'));

    // Strategy 1: Paired paragraphs — numbered descriptor + next paragraph explanation.
    // This is the EXACT format the backend generates:
    //   Para N:   "1   Embrace Divine Invitation:"
    //   Para N+1: "Ramadan is a unique opportunity where..."
    // Track consumed paragraph indices so Strategy 2 doesn't re-capture them.
    const consumedIndices = new Set();

    for (let i = 0; i < paragraphs.length; i++) {
      const text = paragraphs[i].textContent?.trim();
      if (!text) continue;

      // Clean number prefix: "1   Embrace Divine Invitation:" → "Embrace Divine Invitation:"
      const cleaned = text.replace(/^\s*\d+\s+/, '').trim();
      // Descriptor: 5-120 chars ending with colon, standalone paragraph (not a full sentence)
      const isDescriptor = cleaned.endsWith(':') && cleaned.length >= 5 && cleaned.length <= 120;

      if (isDescriptor) {
        // This is a descriptor — find the next non-empty paragraph as explanation
        for (let j = i + 1; j < paragraphs.length && j <= i + 3; j++) {
          const explanation = paragraphs[j].textContent?.trim();
          if (explanation && explanation.length > 30) {
            const combined = `${cleaned} ${explanation}`;
            if (!isMetadata(combined)) {
              takeaways.push(combined);
              consumedIndices.add(i); // Mark descriptor as consumed
              consumedIndices.add(j); // Mark explanation as consumed
            }
            break;
          }
        }
      }
    }

    if (takeaways.length >= 3) return takeaways;

    // Strategy 2: Single-paragraph takeaways with "Descriptor: explanation" inline.
    // Skip paragraphs already consumed by Strategy 1 to prevent split cards.
    paragraphs.forEach((p, idx) => {
      if (consumedIndices.has(idx)) return; // Already part of a Strategy 1 pair
      const text = p.textContent?.trim();
      if (!text || text.length < 50 || isMetadata(text)) return;
      if (takeaways.some(t => text.includes(t.substring(0, 40)))) return; // Already captured

      // "Descriptor: explanation" in one paragraph (colon within first 80 chars, 50+ total)
      const colonIdx = text.indexOf(':');
      if (colonIdx > 3 && colonIdx < 80 && text.length > colonIdx + 30) {
        const cleaned = text.replace(/^[\d•·▪▫◦‣⁃.\)]+\s*/, '').trim();
        if (cleaned.length > 50 && !isMetadata(cleaned)) {
          takeaways.push(cleaned);
        }
      }
    });

    if (takeaways.length >= 3) return takeaways;

    // Strategy 3: List items (some docs use <li> for takeaways)
    const listItems = doc.querySelectorAll('li');
    listItems.forEach(li => {
      const text = li.textContent?.trim();
      if (text && text.length > 50 && text.length < 600 && !isMetadata(text) && isTakeawayContent(text)) {
        const cleaned = text.replace(/^[•·▪▫◦‣⁃\d.\)]+\s*/, '').trim();
        if (!takeaways.includes(cleaned)) takeaways.push(cleaned);
      }
    });

    // NO raw text dump fallback — only structured content passes through

  } catch (error) {
    console.error('Error extracting takeaway points:', error);
  }

  return takeaways;
};

/**
 * Fetches and parses key takeaways from a Google Docs URL
 * @param {string} url - The Google Docs URL
 * @param {string} lectureId - The lecture ID for caching
 * @returns {Promise<string[]>} Array of takeaway points
 */
const fetchAndParseTakeaways = async (url, lectureId) => {
  if (!url) return [];

  // Check cache first
  const cacheKey = `${lectureId}_${url}`;
  const cached = takeawaysCache.get(cacheKey);
  if (cached && cached.timestamp > Date.now() - CACHE_DURATION) {
    return cached.data;
  }

  try {
    // Fetch the document content
    const html = await fetchDocumentContent(url);

    // Extract individual takeaway points
    const takeaways = extractTakeawayPoints(html);

    // Cache the results
    if (takeaways.length > 0) {
      takeawaysCache.set(cacheKey, {
        data: takeaways,
        timestamp: Date.now()
      });
    }

    return takeaways;
  } catch (error) {
    console.error('Error fetching takeaways from:', url, error);
    return [];
  }
};

/**
 * Gets random key takeaways from different lectures
 * @param {string} sheetId - The Google Sheet ID
 * @param {number} count - Number of takeaways to return (default 3)
 * @returns {Promise<Array>} Array of takeaway objects with lecture info
 */
export const getRandomKeyTakeaways = async (sheetId, count = 3) => {
  try {
    console.log('Fetching random key takeaways from sheet:', sheetId);

    // Fetch all lecture data
    const lectures = await fetchLectureData(sheetId);

    // Filter lectures that have key takeaways URLs (both docs.google.com and drive.google.com)
    const lecturesWithTakeaways = lectures.filter(lecture =>
      lecture.keyTakeawaysUrl &&
      (lecture.keyTakeawaysUrl.includes('docs.google.com') || lecture.keyTakeawaysUrl.includes('drive.google.com'))
    );

    if (lecturesWithTakeaways.length === 0) {
      console.warn('No lectures found with key takeaways URLs');
      return [];
    }

    console.log(`Found ${lecturesWithTakeaways.length} lectures with takeaways`);

    // Shuffle the lectures to get random selection
    const shuffled = [...lecturesWithTakeaways].sort(() => Math.random() - 0.5);

    const selectedTakeaways = [];
    const maxAttempts = Math.min(shuffled.length, count * 2);

    for (let i = 0; i < maxAttempts && selectedTakeaways.length < count; i++) {
      const lecture = shuffled[i];

      try {
        const takeaways = await fetchAndParseTakeaways(
          lecture.keyTakeawaysUrl,
          lecture.lectureId
        );

        if (takeaways.length > 0) {
          const randomIndex = Math.floor(Math.random() * takeaways.length);
          const selectedTakeaway = takeaways[randomIndex];

          selectedTakeaways.push({
            text: selectedTakeaway,
            lectureId: lecture.lectureId,
            lectureTitle: lecture.title,
            lectureSpeaker: lecture.speaker,
            lectureDate: lecture.date,
            lectureSeries: lecture.series || lecture.event,
            sourceUrl: lecture.keyTakeawaysUrl
          });
        }
      } catch (error) {
        console.error(`Error processing lecture ${lecture.lectureId}:`, error);
      }
    }

    console.log(`Successfully extracted ${selectedTakeaways.length} takeaways`);
    return selectedTakeaways.slice(0, count);

  } catch (error) {
    console.error('Error getting random key takeaways:', error);
    return [];
  }
};
